import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/events';

const navItems = [
  { to: '/', label: '首页' },
  { to: '/events', label: '事件合集' },
  { to: '/about', label: '关于我们' },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setCatOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(event.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/events?q=${encodeURIComponent(q)}` : '/events');
    setMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/90 backdrop-blur">
      <div className="mx-auto flex h-16 w-full items-center justify-between gap-4 px-4 md:h-20 md:px-6 lg:px-10">
        <Link to="/" className="flex items-center gap-2.5 md:gap-3" aria-label="高校吃瓜社 首页">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50 md:h-11 md:w-11">
            <i className="ri-fire-fill text-xl leading-none" aria-hidden="true"></i>
          </span>
          <span className="flex flex-col leading-none">
            <strong className="font-heading text-lg font-black tracking-tight text-foreground-950 md:text-xl">
              高校吃瓜社
            </strong>
            <span className="mt-0.5 hidden text-[11px] font-medium tracking-[0.2em] text-foreground-500 sm:block">
              围观校园热门现场
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-200 hover:text-foreground-950'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}

          <div className="relative" ref={catRef}>
            <button
              type="button"
              onClick={() => setCatOpen((prev) => !prev)}
              className="flex items-center gap-1 rounded-md px-3.5 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200 hover:text-foreground-950"
              aria-expanded={catOpen}
              aria-haspopup="true"
            >
              话题分类
              <i
                className={`ri-arrow-down-s-line text-base leading-none transition-transform ${catOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              ></i>
            </button>
            {catOpen && (
              <div className="absolute left-0 top-[calc(100%+10px)] w-72 rounded-lg border border-background-200 bg-background-50 p-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/events?cat=${cat.id}`}
                    className="flex items-start gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100"
                  >
                    <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${cat.chip}`}>
                      <i className={`${cat.icon} text-base leading-none`} aria-hidden="true"></i>
                    </span>
                    <span className="flex flex-col">
                      <strong className="text-sm font-semibold text-foreground-950">{cat.name}</strong>
                      <span className="mt-0.5 text-xs leading-snug text-foreground-500">{cat.desc}</span>
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/#submit"
            className="ml-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            我要爆料
          </Link>
        </nav>

        <div className="flex items-center gap-2 md:hidden">
          <Link
            to="/#submit"
            className="whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-background-50"
          >
            爆料
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-foreground-800"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl leading-none`} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <div className="mx-auto w-full px-4 py-4">
            <form onSubmit={handleSearch} className="mb-4 flex items-center gap-2">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                <i className="ri-search-line text-lg leading-none" aria-hidden="true"></i>
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索校园事件 / 话题"
                className="h-10 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
              />
            </form>
            <nav className="flex flex-col gap-1" aria-label="移动端导航">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2.5 text-sm font-medium ${
                      isActive ? 'bg-primary-100 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <p className="mt-4 mb-2 text-xs font-semibold tracking-widest text-foreground-400">话题分类</p>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/events?cat=${cat.id}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium ${cat.chip}`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}